/**
 * @description Pack SO light rail — Untitled look, 357 labels (LYN-360, LYN-361)
 *
 * Key features:
 * - Open rail = logoUrl only; collapsed = logoMarkUrl || symbolUrl; else org initial
 * - Locations heading above the 357 switcher (same treatment as General)
 * - GENERAL: Dashboard, Company (hidden on All), Employees, Settings
 *   Assets & Crew is a Dashboard sub-nav, not a fourth main item.
 * - Footer person card: same /auth/me users row as LynxPay UserProfile / AppHeader
 * - Collapsed: mark + avatar on the w-10 nav-icon axis; chip may stay; no name gutter
 *
 * @dependencies PackCompanySwitcher, AuthContext, CompanyContext, BrandingContext
 *
 * @notes Views consume pack boolean via mount site. No parent-CP slug checks.
 *   Do not copy Untitled item lists (Projects / Documents / Calendar / …).
 */

import React, { useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Building2, ChevronRight, Home, Settings, Users } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useCompany } from '../../../contexts/CompanyContext';
import { useBranding } from '../../../contexts/BrandingContext';
import PackCompanySwitcher from './PackCompanySwitcher';
import { packRailLogo } from './packPersonCard';

const PackSOSidebar = ({ isSidebarOpen, onLinkClick, isMobile = false, ghost = false }) => {
  const { hasCompany } = useCompany();
  const { companyScope, user, salesOrganization } = useAuth();
  const { branding } = useBranding();
  const { pathname } = useLocation();

  const isAll = companyScope === 'all' || !hasCompany;
  const showLabel = isMobile || isSidebarOpen;
  const platformName = branding.identity.platformName
    || salesOrganization?.organizationName
    || 'Organization';
  const railLogo = packRailLogo({
    isOpen: showLabel,
    logoUrl: branding.assets.logoUrl,
    logoMarkUrl: branding.assets.logoMarkUrl,
    symbolUrl: branding.assets.symbolUrl,
    platformName
  });
  // Same /auth/me fields as LynxPay UserProfile (useUser().user === useAuth().user).
  const hasPersonName = Boolean(user?.firstName && user?.lastName);
  const personName = hasPersonName
    ? `${user.firstName} ${user.lastName}`
    : (user?.email || '');
  const personEmail = hasPersonName ? (user?.email || '') : '';
  const personInitials = hasPersonName
    ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
    : (user?.email?.[0]?.toUpperCase() || 'U');

  const packLinks = useMemo(() => {
    const links = [
      { to: '/so/dashboard', label: 'Dashboard', icon: Home }
    ];
    if (hasCompany && !isAll) {
      links.push({ to: '/so/company', label: 'Company', icon: Building2 });
    }
    links.push({ to: '/so/employees', label: 'Employees', icon: Users });
    links.push({ to: '/so/settings', label: 'Settings', icon: Settings });
    return links;
  }, [hasCompany, isAll]);

  return (
    <div
      className={`flex h-full flex-col ${ghost ? 'pointer-events-none select-none' : ''}`}
      data-testid={isMobile ? undefined : 'pack-so-rail'}
      data-ghost-rail={ghost ? 'true' : 'false'}
      aria-hidden={ghost || undefined}
    >
      <div className="px-3 pt-4 pb-3">
        <div className="flex items-center min-h-[2.5rem]" data-testid="pack-rail-header">
          {railLogo.kind === 'logo' && (
            <img
              src={railLogo.src}
              alt={platformName}
              className="h-8 w-auto max-w-[180px] object-contain"
              data-testid="pack-rail-logo"
            />
          )}
          {railLogo.kind === 'mark' && (
            <div
              className="w-10 flex items-center justify-center flex-shrink-0"
              data-testid="pack-rail-mark-slot"
            >
              <img
                src={railLogo.src}
                alt={platformName}
                className="h-8 w-8 object-contain"
                data-testid="pack-rail-mark"
              />
            </div>
          )}
          {railLogo.kind === 'initial' && (
            <span
              className="w-10 h-10 flex items-center justify-center text-brand font-semibold text-lg"
              data-testid="pack-rail-initial"
            >
              {railLogo.initial}
            </span>
          )}
        </div>
      </div>

      {showLabel && (
        <p className="px-5 mb-1 text-[11px] font-semibold tracking-wider text-gray-500 uppercase">
          Locations
        </p>
      )}
      <div className="px-3">
        <PackCompanySwitcher isSidebarOpen={isSidebarOpen} isMobile={isMobile} />
      </div>

      {showLabel && (
        <p className="px-5 mt-2 mb-1 text-[11px] font-semibold tracking-wider text-gray-500 uppercase">
          General
        </p>
      )}

      <nav className="px-3 space-y-0.5">
        {packLinks.map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            onClick={() => isMobile && onLinkClick && onLinkClick()}
            className={({ isActive }) => {
              const dashboardChild = link.to === '/so/dashboard' && pathname === '/so/assets-crew';
              const employeesChild = link.to === '/so/employees' && pathname.startsWith('/so/employees');
              const active = isActive || dashboardChild || employeesChild;
              return `group relative flex items-center py-2 rounded-lg transition-colors duration-150 ${
                active
                  ? 'bg-gray-100 text-gray-900'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`;
            }}
          >
            <div className="w-10 flex items-center justify-center flex-shrink-0">
              <link.icon className="w-5 h-5" />
            </div>
            <span className={`text-sm font-medium overflow-hidden whitespace-nowrap transition-all duration-200 ${
              showLabel ? 'opacity-100 max-w-full' : 'opacity-0 max-w-0'
            }`}>
              {link.label}
            </span>
            {!isMobile && !isSidebarOpen && (
              <div className="absolute left-12 top-1/2 -translate-y-1/2 bg-gray-900 text-white px-2 py-1 rounded text-xs opacity-0 invisible group-hover:opacity-100 group-hover:visible whitespace-nowrap z-50 pointer-events-none">
                {link.label}
              </div>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="mt-auto px-3 pb-3 pt-4">
        <NavLink
          to="/so/profile"
          onClick={() => isMobile && onLinkClick && onLinkClick()}
          className={({ isActive }) =>
            `flex items-center rounded-xl border border-gray-200 bg-white py-2 ${
              showLabel ? 'gap-2 px-2' : 'justify-center'
            } ${isActive ? 'bg-gray-50' : 'hover:bg-gray-50'}`
          }
          aria-label="Profile"
          data-testid="pack-person-card"
          data-collapsed={showLabel ? 'false' : 'true'}
        >
          {user?.imageUrl ? (
            <img
              src={user.imageUrl}
              alt=""
              className="w-9 h-9 rounded-full object-cover flex-shrink-0 border border-gray-200"
            />
          ) : (
            <div className="w-9 h-9 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-xs font-semibold text-gray-700 flex-shrink-0">
              {personInitials}
            </div>
          )}
          {showLabel && (
            <>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-gray-900 truncate">{personName}</div>
                {personEmail ? (
                  <div className="text-xs text-gray-500 truncate">{personEmail}</div>
                ) : null}
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
            </>
          )}
        </NavLink>
      </div>
    </div>
  );
};

export default PackSOSidebar;
